import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkline } from "../analytics/_utils";
import { CustomIcon } from "../shared/CustomIcon";
import EmptyState from "../shared/EmptyState";

// =============================================================
// NotificationsArchivePage — shows ALL notifications (no 30-day cut-off
// like the bell). Filterable by the same 7 section categories. Each row
// is clickable and routes through the parent's handleNotifClick.
// =============================================================

const SECTIONS = [
  { id: "all",           label: "All",           customIcon: "bell",          types: null }, // null => show all
  { id: "announcements", label: "Announcements", customIcon: "megaphone",
    types: ["department_announcement", "announcement_reminder"] },
  { id: "listings",      label: "Listings",      customIcon: "gift",
    types: ["donation_listing", "donation_intake"] },
  { id: "assignments",   label: "Assignments",   customIcon: "person",
    types: ["assignment_added", "assignment_removed", "assignment_changed", "assignment_prewarn", "assignment_expired"] },
  { id: "calendar",      label: "Calendar",      customIcon: "calendar",
    types: ["service", "event", "blockout", "appointment", "attendance"] },
  { id: "followups",     label: "Follow-Ups",    customIcon: "chat",
    types: ["followup", "followup_close_request", "followup_close_approved", "followup_close_rejected"] },
  { id: "birthdays",     label: "Birthdays",     customIcon: "birthday-cake",
    types: ["birthday"] },
  { id: "overrides",     label: "Overrides",     customIcon: "warning",
    types: ["override_request", "override_approved", "override_denied", "priority_revert"] },
];

function formatStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function NotificationsArchivePage({ notifications, currentUser, handleNotifClick, queueDeletion }) {
  const navigate = useNavigate();
  const [sectionId, setSectionId] = useState("all");

  const sorted = useMemo(() => {
    return (notifications || []).slice().sort((a, b) => (b.createdAt || b.time || "").localeCompare(a.createdAt || a.time || ""));
  }, [notifications]);

  const filtered = useMemo(() => {
    const sec = SECTIONS.find(s => s.id === sectionId);
    if (!sec || !sec.types) return sorted;
    const set = new Set(sec.types);
    return sorted.filter(n => set.has(n.type));
  }, [sorted, sectionId]);

  // Build a 14-day notification volume series for the sparkline. Index 0 is
  // 13 days ago, index 13 is today. Counts every notif that lands in the
  // currently-active filter so the trend reflects the user's current focus.
  const volumeTrend = useMemo(() => {
    const days = 14;
    const buckets = new Array(days).fill(0);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const startMs = today.getTime() - (days - 1) * 86400000;
    filtered.forEach(n => {
      const iso = n.createdAt || n.time;
      if (!iso) return;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return;
      const idx = Math.floor((d.getTime() - startMs) / 86400000);
      if (idx >= 0 && idx < days) buckets[idx] += 1;
    });
    return buckets;
  }, [filtered]);

  const isRead = (n) =>
    Array.isArray(n.read) ? n.read.includes(currentUser?.id) : !!n.read;

  return (
    <div data-testid="notifications-archive-page" style={{ background: "var(--kc-bg)", minHeight: "100vh", padding: "24px clamp(16px,4vw,40px)" }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button
            className="btn btn-outline btn-sm"
            data-testid="notif-archive-back"
            onClick={() => navigate("/dashboard")}
            style={{ flexShrink: 0 }}
          >← Back</button>
          <div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              All Notifications
            </div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-sub)" }}>
              {sorted.length} total · including archived (older than 30 days)
            </div>
          </div>
          {/* 14-day volume sparkline — quick visual of incoming notification rate. */}
          <div data-testid="notif-archive-trend-wrap" style={{ marginLeft: "auto", textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: "color-mix(in srgb, var(--kc-accent) 85%, transparent)", textTransform: "uppercase", letterSpacing: "0.10em", fontWeight: 700, marginBottom: 4 }}>14-Day Trend</div>
            <Sparkline data={volumeTrend} testId="notif-archive-trend" width={140} height={28} />
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "var(--kc-meta)", marginTop: 2 }}>{volumeTrend.reduce((a, b) => a + b, 0)} in last 14 days</div>
          </div>
        </div>

        {/* Section filter pills */}
        <div
          data-testid="notif-archive-filter-bar"
          style={{
            display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18,
            padding: "12px 14px", background: "var(--kc-surface)",
            border: "1px solid var(--kc-border)", borderRadius: 12,
          }}
        >
          {SECTIONS.map(s => {
            const active = sectionId === s.id;
            const count = s.types === null ? sorted.length : sorted.filter(n => s.types.includes(n.type)).length;
            return (
              <button
                key={s.id}
                data-testid={`notif-archive-filter-${s.id}`}
                onClick={() => setSectionId(s.id)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 999,
                  background: active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                  color: active ? "var(--kc-on-accent)" : "var(--kc-text)",
                  border: `1px solid ${active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 40%, transparent)"}`,
                  fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700,
                  letterSpacing: "0.04em", textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 14, display: "inline-flex", alignItems: "center" }}>
                  {s.customIcon ? <CustomIcon name={s.customIcon} size={16} /> : s.icon}
                </span>
                <span>{s.label}</span>
                <span style={{
                  minWidth: 18, height: 18, lineHeight: "18px", padding: "0 5px",
                  background: active ? "var(--kc-on-accent)" : "color-mix(in srgb, var(--kc-accent) 20%, transparent)",
                  color: active ? "var(--kc-accent)" : "var(--kc-text)",
                  borderRadius: 999, fontSize: 10, textAlign: "center",
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <EmptyState
            data-testid="notif-archive-empty"
            icon="bell"
            title="No notifications in this section"
            subtitle="New notifications will appear here as they arrive."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map(n => {
              const read = isRead(n);
              // Semantic variant per notification type
              const t = String(n.type || "").toLowerCase();
              const isUrgent  = /crisis|urgent|error|deletion|denied|reject/.test(t);
              const isWarning = /warning|override|pending|reminder|overdue/.test(t);
              const variant = isUrgent ? "red" : isWarning ? "amber" : "blue";
              const borderColor = variant === "red" ? "var(--kc-red)" : variant === "amber" ? "var(--kc-amber)" : "var(--kc-accent)";
              return (
                <div
                  key={n.id}
                  data-testid={`notif-archive-item-${n.id}`}
                  className={`card-${variant}`}
                  data-variant={variant}
                  style={{
                    position: "relative",
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "12px 46px 12px 14px", textAlign: "left",
                    background: read ? "var(--kc-surface)" : "color-mix(in srgb, var(--kc-accent) 6%, var(--kc-surface))",
                    border: `1px solid var(--kc-border)`,
                    borderLeft: `3px solid ${borderColor}`,
                    borderRadius: 10, color: "var(--kc-text)",
                    fontFamily: "'Inter',sans-serif",
                  }}
                >
                  <div
                    role="button" tabIndex={0}
                    onClick={() => handleNotifClick && handleNotifClick(n)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleNotifClick && handleNotifClick(n); } }}
                    style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: 1, minWidth: 0, cursor: "pointer" }}
                  >
                    {!read && (
                      <span style={{ marginTop: 6, display: "inline-block", width: 8, height: 8, background: "var(--kc-accent)", borderRadius: "50%", flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, lineHeight: 1.45, marginBottom: 4 }}>{n.text}</div>
                      <div style={{ fontSize: 10, color: "var(--kc-sub)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        {n.type || "general"} · {formatStamp(n.createdAt || n.time)}
                      </div>
                    </div>
                  </div>
                  {queueDeletion && (
                    <button
                      data-testid={`notif-archive-delete-${n.id}`}
                      onClick={(e) => { e.stopPropagation(); queueDeletion([n]); }}
                      aria-label="Delete notification"
                      title="Delete"
                      style={{
                        position: "absolute", top: "50%", right: 8, transform: "translateY(-50%)",
                        width: 28, height: 28, borderRadius: "50%",
                        background: "rgba(184,50,50,0.18)", border: "1px solid rgba(184,50,50,0.30)",
                        color: "#ff6b6b", fontSize: 14, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
